import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Table, Td, Th } from "@/components/ui/Table";
import { useSync } from "@/hooks/useSync";
import { db } from "@/lib/db";
import { formatDateTime } from "@/lib/format";
import { strings } from "@/lib/strings";
import type { LocalIsolate } from "@/lib/types";

/**
 * The sync queue (docs/09 §9.1, principle 2: no silent loss).
 *
 * Everything the device is holding, why anything was rejected, and what the
 * volunteer can do about it.
 */
export function SyncQueue() {
  const sync = useSync();
  const [rows, setRows] = useState<LocalIsolate[]>([]);

  const load = useCallback(async () => {
    const all = await db.isolates_local.orderBy("updated_at").reverse().toArray();
    setRows(all);
  }, []);

  useEffect(() => {
    void load();
  }, [load, sync.pending, sync.conflicts, sync.running]);

  const problems = rows.filter((row) => row.state === "conflict" || row.state === "error");
  const waiting = rows.filter((row) => row.state === "pending" || row.state === "syncing");
  const done = rows.filter((row) => row.state === "synced");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-slate-900">Sync queue</h1>
        <Button
          variant="secondary"
          size="sm"
          disabled={!sync.online || sync.running}
          loading={sync.running}
          onClick={() => void sync.sync()}
        >
          Sync now
        </Button>
      </div>

      {sync.lastError && (
        <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">{sync.lastError}</p>
      )}

      {problems.length > 0 && (
        <Card>
          <CardHeader
            title={`Needs your attention (${problems.length})`}
            description="The server would not accept these. They are still here - nothing has been lost."
          />
          <Table caption="Rejected records">
            <thead>
              <tr>
                <Th>Saved</Th>
                <Th>Lab number</Th>
                <Th>What the server said</Th>
                <Th>
                  <span className="sr-only">Actions</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {problems.map((row) => (
                <tr key={row.client_uuid}>
                  <Td className="whitespace-nowrap text-xs">
                    {formatDateTime(row.client_created_at)}
                  </Td>
                  <Td>{row.payload.lab_number ?? "—"}</Td>
                  <Td className="text-sm text-red-800">{row.error}</Td>
                  <Td className="whitespace-nowrap">
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => void sync.retry(row.client_uuid)}>
                        Try again
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => void sync.discard(row.client_uuid)}>
                        Discard
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      <Card>
        <CardHeader title={`Waiting to upload (${waiting.length})`} />
        {waiting.length === 0 ? (
          <EmptyState title={strings.sync.nothingPending} />
        ) : (
          <Table caption="Waiting to upload">
            <thead>
              <tr>
                <Th>Saved</Th>
                <Th>Lab number</Th>
                <Th>Organism</Th>
                <Th>State</Th>
              </tr>
            </thead>
            <tbody>
              {waiting.map((row) => (
                <tr key={row.client_uuid}>
                  <Td className="whitespace-nowrap text-xs">
                    {formatDateTime(row.client_created_at)}
                  </Td>
                  <Td>{row.payload.lab_number ?? "—"}</Td>
                  <Td className="italic">{row.payload.organism_code ?? "—"}</Td>
                  <Td>
                    <Badge tone={row.state === "syncing" ? "info" : "warning"}>
                      {row.state === "syncing" ? "Uploading" : strings.sync.pendingBadge}
                    </Badge>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {done.length > 0 && (
        <Card>
          <CardHeader
            title={`Uploaded (${done.length})`}
            description="Kept on this device for 30 days, then removed. They remain on the server."
          />
          <CardBody>
            <ul className="flex flex-wrap gap-2">
              {done.slice(0, 50).map((row) => (
                <li key={row.client_uuid}>
                  <Badge tone="ok">{row.record_id ?? "saved"}</Badge>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
